import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Link,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  AutoGraph as AiUsageIcon,
} from '@mui/icons-material';
import { useTenant } from '../../contexts/TenantContext';
import useApi from '../../hooks/useApi';
import { Pagination } from '../../components/common';
import ClientForm from './ClientForm';
import ClientUsersDialog from './ClientUsersDialog';
import ClientFeaturesDialog from './ClientFeaturesDialog';
import ClientAiUsageDialog from './ClientAiUsageDialog';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';
import {
  USAGE_SEVERITY_COLORS,
  formatTokensCompact,
  getUsageSeverity,
} from '../../constants/aiUsage';
import {
  SurveyRuntimeApiProvider,
  useSurveyRuntimeApi,
} from '../../surveys/context/SurveyRuntimeApiContext';
import SurveyLogoEditor from '../../surveys/components/SurveyLogoEditor';
import './ClientsPage.css';
 
// One logo per client tenant (survey-runtime DB), used by every survey that
// tenant owns. Separate provider scope: this page otherwise talks to SDA, not
// survey-runtime (same pattern as Groups page).
function ClientLogoDialogBody({ tenantId, onClose }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const [logoImage, setLogoImage] = useState(null);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/admin/clients/${tenantId}/logo`);
        if (!cancelled) setLogoImage(res?.logo_image ?? null);
      } catch {
        /* no logo yet — leave null */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, tenantId]);
 
  return (
    <>
      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <SurveyLogoEditor
            tenantId={tenantId}
            logoImage={logoImage}
            onChange={(next) => setLogoImage(next.logo_image ?? null)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
}
 
const ITEMS_PER_PAGE = 10;
 
const ClientsPage = () => {
  const api = useApi();
  const { isJLLUser, hasRole } = useTenant();
  const [clients, setClients] = useState([]);
  const [logoDialogClient, setLogoDialogClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, client: null });
  const [usersDialog, setUsersDialog] = useState({ open: false, client: null });
  const [featuresDialog, setFeaturesDialog] = useState({ open: false, client: null });
  const [aiUsageDialog, setAiUsageDialog] = useState({ open: false, client: null });
  const { showErrorToast } = useErrorToast();
 
  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);
 
  useEffect(() => {
    if (!isJLLUser() || !hasRole('JLL Super Admin')) {
      showErrorToast('Access denied. Only JLL Super Admin can manage clients.');
    }
  }, [isJLLUser, hasRole, showErrorToast]);
 
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
     
      const response = await api.get('/tenants', {
        params: {
          page: page,
          pageSize: ITEMS_PER_PAGE,
        },
      });
 
      if (response.data?.success) {
        const data = response.data.data;
        setClients(data.tenants || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalItems(data.pagination?.totalItems || 0);
      } else {
        setError(getApiErrorMessage(null, 'Failed to load clients'));
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(getApiErrorMessage(err, 'Failed to load clients'));
    } finally {
      setLoading(false);
    }
  }, [api, page]);
 
  useEffect(() => {
    if (isJLLUser() && hasRole('JLL Super Admin')) {
      loadClients();
    }
  }, [isJLLUser, hasRole, loadClients]);
 
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
 
  const handleAddNew = () => {
    setEditingClient(null);
    setOpenFormDialog(true);
  };
 
  const handleEdit = (client) => {
    setEditingClient(client);
    setOpenFormDialog(true);
  };
 
  const handleDelete = (client) => {
    setDeleteDialog({ open: true, client });
  };
 
  const handleShowUsers = (client) => {
    setUsersDialog({ open: true, client });
  };
 
  const handleManageFeatures = (client) => {
    setFeaturesDialog({ open: true, client });
  };

  const handleManageAiUsage = (client) => {
    setAiUsageDialog({ open: true, client });
  };
 
  const confirmDelete = async () => {
    try {
      setError(null);
      const client = deleteDialog.client;
     
      const response = await api.put(`/tenants/${client.TenantId}`, {
        tenantName: client.TenantName,
        isActive: false,
      });
 
      if (response.data?.success) {
        setSuccess(`Client "${client.TenantName}" has been deactivated successfully`);
        setDeleteDialog({ open: false, client: null });
        loadClients();
      } else {
        setError(getApiErrorMessage(null, 'Failed to deactivate client'));
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      setError(getApiErrorMessage(err, 'Failed to deactivate client'));
    }
  };
 
  const handleFormClose = () => {
    setOpenFormDialog(false);
    setEditingClient(null);
  };
 
  const handleFormSuccess = () => {
    setOpenFormDialog(false);
    setEditingClient(null);
    loadClients();
  };
 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };
 
  /**
   * AI allowance at a glance. The tenants list joins in the limits and running
   * totals, so the whole grid renders from one request; the link opens the full
   * usage view for that client.
   */
  const renderAiTokenCell = (client) => {
    if (!client.AITokenLimitConfigured) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleManageAiUsage(client)}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: '#1976d2',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <AiUsageIcon fontSize="small" />
          Set limits
        </Link>
      );
    }

    const threshold = Number(client.AIAlertThresholdPercent) || 80;
    const inputLimit = Number(client.AIInputTokenLimit) || 0;
    const outputLimit = Number(client.AIOutputTokenLimit) || 0;
    const inputPercent = inputLimit ? (Number(client.AIInputTokensUsed) || 0) / inputLimit * 100 : 0;
    const outputPercent = outputLimit ? (Number(client.AIOutputTokensUsed) || 0) / outputLimit * 100 : 0;
    // The client is only as healthy as its worst meter
    const peakPercent = Math.max(inputPercent, outputPercent);
    const { main: severityColor, label: severityLabel } =
      USAGE_SEVERITY_COLORS[getUsageSeverity(peakPercent, threshold)];

    return (
      <Link
        component="button"
        variant="body2"
        onClick={() => handleManageAiUsage(client)}
        title={`${severityLabel} — click to view usage and adjust limits`}
        sx={{
          cursor: 'pointer',
          textDecoration: 'none',
          color: '#1976d2',
          fontWeight: 500,
          textAlign: 'left',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: severityColor,
              flexShrink: 0,
            }}
          />
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {formatTokensCompact(inputLimit)} in / {formatTokensCompact(outputLimit)} out
          </Box>
        </Box>
      </Link>
    );
  };

  if (!isJLLUser() || !hasRole('JLL Super Admin')) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 80px)',
        p: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },
        margin: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          }}
        >
          Clients Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{
            backgroundColor: '#000000',
            '&:hover': {
              backgroundColor: '#333333',
            },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Add New Client
        </Button>
      </Box>
 
 
      <TableContainer
        component={Paper}
        elevation={1}
        sx={{
          borderRadius: '8px',
          overflow: 'auto',
          maxWidth: '100%',
        }}
      >
        <Table
          sx={{
            minWidth: 650,
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                Client Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                Client Code
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                Users
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                Features
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                AI Tokens
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  display: { xs: 'none', md: 'table-cell' },
                }}
              >
                Added Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  display: { xs: 'none', lg: 'table-cell' },
                }}
              >
                Added By
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No clients found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.TenantId} hover>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    {client.TenantName}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    {client.TenantCode}
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleShowUsers(client)}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Show Users
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleManageFeatures(client)}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                      Manage Features
                    </Link>
                  </TableCell>
                  <TableCell>{renderAiTokenCell(client)}</TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      display: { xs: 'none', md: 'table-cell' },
                    }}
                  >
                    {formatDate(client.CreatedDate)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      display: { xs: 'none', lg: 'table-cell' },
                    }}
                  >
                    {client.CreatedBy || 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => setLogoDialogClient(client)}
                      size="small"
                      sx={{ mr: { xs: 0.5, sm: 1 } }}
                      title="Client logo"
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleEdit(client)}
                      size="small"
                      color="primary"
                      sx={{ mr: { xs: 0.5, sm: 1 } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(client)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
 
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={ITEMS_PER_PAGE}
          onChange={handlePageChange}
        />
      </Box>
 
      <ClientForm
        open={openFormDialog}
        client={editingClient}
        onClose={handleFormClose}
        onSubmitSuccess={handleFormSuccess}
      />
 
      <ClientUsersDialog
        open={usersDialog.open}
        client={usersDialog.client}
        onClose={() => setUsersDialog({ open: false, client: null })}
      />
 
      <ClientFeaturesDialog
        open={featuresDialog.open}
        client={featuresDialog.client}
        onClose={() => setFeaturesDialog({ open: false, client: null })}
      />

      <ClientAiUsageDialog
        open={aiUsageDialog.open}
        client={aiUsageDialog.client}
        onClose={() => setAiUsageDialog({ open: false, client: null })}
        onSaved={loadClients}
      />
 
      <Dialog
        open={!!logoDialogClient}
        onClose={() => setLogoDialogClient(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Logo — {logoDialogClient?.TenantName}</DialogTitle>
        {logoDialogClient && (
          <SurveyRuntimeApiProvider>
            <ClientLogoDialogBody
              tenantId={logoDialogClient.TenantId}
              onClose={() => setLogoDialogClient(null)}
            />
          </SurveyRuntimeApiProvider>
        )}
      </Dialog>
 
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, client: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate client "{deleteDialog.client?.TenantName}"?
            This will soft delete the client and deactivate all associated users.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, client: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
 
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};
 
export default ClientsPage;